import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import type { AccountPrompt } from '@api/client'
import type { TargetAccount, Owner } from '../types'

// =====================================================
// MARKDOWN → PLAIN TEXT PARSER
// =====================================================

interface ParsedLine {
  text: string
  style: 'h1' | 'h2' | 'h3' | 'body' | 'bullet' | 'numbered' | 'separator'
  indent: number
}

function parseMarkdownForPDF(markdown: string): ParsedLine[] {
  const lines: ParsedLine[] = []
  const rawLines = markdown.split('\n')

  for (const raw of rawLines) {
    const trimmed = raw.trim()
    if (!trimmed) continue

    // Horizontal rules
    if (/^[-*_]{3,}$/.test(trimmed)) {
      lines.push({ text: '', style: 'separator', indent: 0 })
      continue
    }

    // Headers
    if (trimmed.startsWith('### ')) {
      lines.push({ text: stripMarkdown(trimmed.slice(4)), style: 'h3', indent: 0 })
      continue
    }
    if (trimmed.startsWith('## ')) {
      lines.push({ text: stripMarkdown(trimmed.slice(3)), style: 'h2', indent: 0 })
      continue
    }
    if (trimmed.startsWith('# ')) {
      lines.push({ text: stripMarkdown(trimmed.slice(2)), style: 'h1', indent: 0 })
      continue
    }

    // Bullet points (with indent detection)
    const bulletMatch = trimmed.match(/^(\s*)[*-]\s+(.+)/)
    if (bulletMatch) {
      const indent = Math.floor((raw.search(/\S/) || 0) / 2)
      lines.push({ text: '• ' + stripMarkdown(bulletMatch[2]), style: 'bullet', indent })
      continue
    }

    // Numbered lists
    const numberedMatch = trimmed.match(/^(\d+)\.\s+(.+)/)
    if (numberedMatch) {
      lines.push({ text: numberedMatch[1] + '. ' + stripMarkdown(numberedMatch[2]), style: 'numbered', indent: 0 })
      continue
    }

    // Regular text
    lines.push({ text: stripMarkdown(trimmed), style: 'body', indent: 0 })
  }

  return lines
}

function stripMarkdown(text: string): string {
  return text
    .replace(/<br\s*\/?>/gi, '\n')        // <br> and <br/> → newline
    .replace(/<[^>]+>/g, '')              // Strip all remaining HTML tags
    .replace(/\*\*(.+?)\*\*/g, '$1')     // Bold
    .replace(/\*(.+?)\*/g, '$1')          // Italic
    .replace(/__(.+?)__/g, '$1')          // Bold underscore
    .replace(/_(.+?)_/g, '$1')            // Italic underscore
    .replace(/`(.+?)`/g, '$1')            // Inline code
    .replace(/\[(.+?)\]\(.+?\)/g, '$1')   // Links → just the text
    .replace(/~~(.+?)~~/g, '$1')          // Strikethrough
    .replace(/&amp;/g, '&')              // HTML entities
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .trim()
}

// =====================================================
// MARKDOWN TABLE DETECTION
// =====================================================

interface MarkdownTable {
  headers: string[]
  rows: string[][]
}

function extractTables(markdown: string): { tables: MarkdownTable[], textWithoutTables: string } {
  const tables: MarkdownTable[] = []
  const lines = markdown.split('\n')
  const nonTableLines: string[] = []
  let i = 0

  while (i < lines.length) {
    // Detect table: line with pipes, followed by separator line with dashes
    if (
      i + 1 < lines.length &&
      lines[i].includes('|') &&
      lines[i + 1].includes('|') &&
      /^[\s|:-]+$/.test(lines[i + 1])
    ) {
      const headers = lines[i].split('|').map(h => h.trim()).filter(Boolean)
      i += 2 // Skip header + separator

      const rows: string[][] = []
      while (i < lines.length && lines[i].includes('|') && !(/^[\s|:-]+$/.test(lines[i]) && !lines[i].match(/[a-zA-Z0-9]/))) {
        const row = lines[i].split('|').map(c => c.trim()).filter(Boolean)
        if (row.length > 0) rows.push(row)
        i++
      }

      tables.push({ headers, rows })
      nonTableLines.push(`[TABLE_${tables.length - 1}]`)
    } else {
      nonTableLines.push(lines[i])
      i++
    }
  }

  return { tables, textWithoutTables: nonTableLines.join('\n') }
}

// =====================================================
// SINGLE RESPONSE → PDF
// =====================================================

export function exportResponseToPDF(item: AccountPrompt, accountName: string): void {
  const doc = new jsPDF()
  const pageWidth = doc.internal.pageSize.getWidth()
  const margin = 14
  const contentWidth = pageWidth - margin * 2
  let yPos = 20

  const dateStr = new Date(item.created_at).toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

  // Header
  doc.setFontSize(16)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(68, 114, 196)
  doc.text('DealHub AI Assistant', margin, yPos)
  yPos += 7

  doc.setFontSize(10)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(100, 100, 100)
  doc.text(`Account: ${accountName}  |  ${dateStr}`, margin, yPos)
  if (item.user_name || item.user_email) {
    yPos += 5
    doc.text(`Asked by: ${item.user_name || item.user_email}`, margin, yPos)
  }
  yPos += 10

  // Question box
  doc.setFillColor(240, 244, 248)
  doc.setDrawColor(200, 210, 220)
  const questionLines = doc.splitTextToSize(item.prompt, contentWidth - 10)
  const questionHeight = questionLines.length * 5 + 10
  doc.roundedRect(margin, yPos, contentWidth, questionHeight, 2, 2, 'FD')
  doc.setFontSize(9)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(68, 114, 196)
  doc.text('QUESTION', margin + 5, yPos + 6)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(40, 40, 40)
  doc.text(questionLines, margin + 5, yPos + 12)
  yPos += questionHeight + 8

  // Separator
  doc.setDrawColor(200, 210, 220)
  doc.line(margin, yPos, pageWidth - margin, yPos)
  yPos += 8

  // AI Response
  doc.setFontSize(9)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(68, 114, 196)
  doc.text('AI RESPONSE', margin, yPos)
  yPos += 6

  // Extract tables for separate rendering
  const { tables, textWithoutTables } = extractTables(item.response)
  const parsed = parseMarkdownForPDF(textWithoutTables)

  for (const line of parsed) {
    // Check for page break
    if (yPos > 270) {
      doc.addPage()
      yPos = 20
    }

    // Check for table placeholder
    const tableMatch = line.text.match(/\[TABLE_(\d+)\]/)
    if (tableMatch || line.style === 'body' && line.text.match(/^\[TABLE_\d+\]$/)) {
      const tableIdx = parseInt(tableMatch?.[1] || '0')
      const table = tables[tableIdx]
      if (table) {
        yPos += 2
        autoTable(doc, {
          startY: yPos,
          head: [table.headers.map(h => stripMarkdown(h))],
          body: table.rows.map(row => row.map(cell => stripMarkdown(cell))),
          theme: 'grid',
          headStyles: {
            fillColor: [68, 114, 196],
            textColor: 255,
            fontSize: 8,
            fontStyle: 'bold',
            halign: 'left',
          },
          bodyStyles: {
            fontSize: 8,
            cellPadding: 2.5,
            textColor: [40, 40, 40],
          },
          alternateRowStyles: {
            fillColor: [245, 247, 250],
          },
          margin: { left: margin, right: margin },
        })
        // @ts-expect-error - jspdf-autotable extends doc
        yPos = doc.lastAutoTable.finalY + 6
        continue
      }
    }

    const xOffset = margin + (line.indent * 6)

    switch (line.style) {
      case 'h1':
        yPos += 3
        doc.setFontSize(13)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(30, 30, 30)
        doc.text(line.text, margin, yPos)
        yPos += 7
        break
      case 'h2':
        yPos += 2
        doc.setFontSize(11)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(68, 114, 196)
        doc.text(line.text, margin, yPos)
        yPos += 6
        break
      case 'h3':
        yPos += 1
        doc.setFontSize(10)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(50, 50, 50)
        doc.text(line.text, margin, yPos)
        yPos += 5.5
        break
      case 'bullet':
      case 'numbered': {
        doc.setFontSize(9)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(40, 40, 40)
        const wrapped = doc.splitTextToSize(line.text, contentWidth - (line.indent * 6) - 4)
        doc.text(wrapped, xOffset, yPos)
        yPos += wrapped.length * 4.2
        break
      }
      case 'separator':
        doc.setDrawColor(200, 210, 220)
        doc.line(margin, yPos, pageWidth - margin, yPos)
        yPos += 4
        break
      default: {
        doc.setFontSize(9)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(40, 40, 40)
        const bodyWrapped = doc.splitTextToSize(line.text, contentWidth - 4)
        doc.text(bodyWrapped, margin, yPos)
        yPos += bodyWrapped.length * 4.2
        break
      }
    }
  }

  // Footer on last page
  addFooter(doc)

  const safeAccountName = accountName.replace(/[^a-zA-Z0-9]/g, '_')
  const dateFile = new Date(item.created_at).toISOString().split('T')[0]
  doc.save(`DealHub_AI_${safeAccountName}_${dateFile}.pdf`)
}

// =====================================================
// CONVERSATION HISTORY → PDF
// =====================================================

export function exportHistoryToPDF(items: AccountPrompt[], accountName: string): void {
  const doc = new jsPDF()
  const pageWidth = doc.internal.pageSize.getWidth()
  const margin = 14
  const contentWidth = pageWidth - margin * 2
  let yPos = 20

  // Title page header
  doc.setFontSize(18)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(68, 114, 196)
  doc.text('DealHub AI Assistant', margin, yPos)
  yPos += 8

  doc.setFontSize(12)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(80, 80, 80)
  doc.text(`Conversation History — ${accountName}`, margin, yPos)
  yPos += 6

  doc.setFontSize(10)
  doc.text(`${items.length} conversation${items.length !== 1 ? 's' : ''} exported on ${new Date().toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' })}`, margin, yPos)
  yPos += 10

  // Summary table
  autoTable(doc, {
    startY: yPos,
    head: [['#', 'Date', 'Question (preview)', 'Tokens']],
    body: items.map((item, idx) => [
      (idx + 1).toString(),
      new Date(item.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }),
      item.prompt.length > 80 ? item.prompt.slice(0, 77) + '...' : item.prompt,
      item.tokens_used?.toString() || '-',
    ]),
    theme: 'grid',
    headStyles: {
      fillColor: [68, 114, 196],
      textColor: 255,
      fontSize: 8,
      fontStyle: 'bold',
    },
    bodyStyles: {
      fontSize: 8,
      cellPadding: 2.5,
    },
    columnStyles: {
      0: { cellWidth: 10 },
      1: { cellWidth: 28 },
      3: { cellWidth: 18, halign: 'right' },
    },
    margin: { left: margin, right: margin },
  })

  // @ts-expect-error - jspdf-autotable extends doc
  yPos = doc.lastAutoTable.finalY + 10

  // Each conversation
  items.forEach((item, idx) => {
    // Always start each conversation on a new page (except if we're already near the top)
    if (yPos > 60 || idx > 0) {
      doc.addPage()
      yPos = 20
    }

    const dateStr = new Date(item.created_at).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })

    // Conversation header
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(68, 114, 196)
    doc.text(`Conversation ${idx + 1}`, margin, yPos)
    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(120, 120, 120)
    doc.text(dateStr, pageWidth - margin - doc.getTextWidth(dateStr), yPos)
    yPos += 8

    // Question
    doc.setFillColor(240, 244, 248)
    doc.setDrawColor(200, 210, 220)
    const questionLines = doc.splitTextToSize(item.prompt, contentWidth - 10)
    const qHeight = questionLines.length * 4.2 + 10
    doc.roundedRect(margin, yPos, contentWidth, qHeight, 2, 2, 'FD')
    doc.setFontSize(8)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(68, 114, 196)
    doc.text('QUESTION', margin + 5, yPos + 5)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(40, 40, 40)
    doc.setFontSize(9)
    doc.text(questionLines, margin + 5, yPos + 10)
    yPos += qHeight + 6

    // Response (simplified — rendered as wrapped text to keep multi-convo PDF manageable)
    doc.setFontSize(8)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(68, 114, 196)
    doc.text('AI RESPONSE', margin, yPos)
    yPos += 5

    const { tables, textWithoutTables } = extractTables(item.response)
    const parsed = parseMarkdownForPDF(textWithoutTables)

    for (const line of parsed) {
      if (yPos > 270) {
        doc.addPage()
        yPos = 20
      }

      const tableMatch = line.text.match(/\[TABLE_(\d+)\]/)
      if (tableMatch) {
        const table = tables[parseInt(tableMatch[1])]
        if (table) {
          autoTable(doc, {
            startY: yPos,
            head: [table.headers.map(h => stripMarkdown(h))],
            body: table.rows.map(r => r.map(c => stripMarkdown(c))),
            theme: 'grid',
            headStyles: { fillColor: [68, 114, 196], textColor: 255, fontSize: 7, fontStyle: 'bold' },
            bodyStyles: { fontSize: 7, cellPadding: 2 },
            margin: { left: margin, right: margin },
          })
          // @ts-expect-error - jspdf-autotable extends doc
          yPos = doc.lastAutoTable.finalY + 4
          continue
        }
      }

      const xOffset = margin + (line.indent * 5)

      switch (line.style) {
        case 'h1':
        case 'h2':
          yPos += 2
          doc.setFontSize(line.style === 'h1' ? 11 : 10)
          doc.setFont('helvetica', 'bold')
          doc.setTextColor(line.style === 'h1' ? 30 : 68, line.style === 'h1' ? 30 : 114, line.style === 'h1' ? 30 : 196)
          doc.text(line.text, margin, yPos)
          yPos += line.style === 'h1' ? 6 : 5.5
          break
        case 'h3':
          doc.setFontSize(9)
          doc.setFont('helvetica', 'bold')
          doc.setTextColor(50, 50, 50)
          doc.text(line.text, margin, yPos)
          yPos += 5
          break
        case 'separator':
          doc.setDrawColor(200, 210, 220)
          doc.line(margin, yPos, pageWidth - margin, yPos)
          yPos += 3
          break
        default: {
          doc.setFontSize(8.5)
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(40, 40, 40)
          const w = doc.splitTextToSize(line.text, contentWidth - (line.indent * 5))
          doc.text(w, xOffset, yPos)
          yPos += w.length * 3.8
          break
        }
      }
    }

    // Token count
    if (item.tokens_used) {
      yPos += 4
      doc.setFontSize(7)
      doc.setTextColor(150, 150, 150)
      doc.text(`${item.tokens_used} tokens | Model: ${item.model || 'claude'}`, margin, yPos)
    }
  })

  // Add footer to all pages
  addFooter(doc)

  const safeAccountName = accountName.replace(/[^a-zA-Z0-9]/g, '_')
  const dateFile = new Date().toISOString().split('T')[0]
  doc.save(`DealHub_AI_History_${safeAccountName}_${dateFile}.pdf`)
}

// =====================================================
// SINGLE RESPONSE → CSV
// =====================================================

export function exportResponseToCSV(item: AccountPrompt, accountName: string): void {
  const escCSV = (val: string) => `"${val.replace(/"/g, '""')}"`

  const headers = ['Date', 'Account', 'Asked By', 'Question', 'Response', 'Model', 'Tokens']
  const row = [
    new Date(item.created_at).toISOString(),
    accountName,
    item.user_name || item.user_email,
    item.prompt,
    item.response,
    item.model || '',
    item.tokens_used?.toString() || '',
  ]

  const csv = headers.join(',') + '\n' + row.map(escCSV).join(',')
  downloadCSV(csv, `DealHub_AI_${accountName.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date(item.created_at).toISOString().split('T')[0]}.csv`)
}

// =====================================================
// CONVERSATION HISTORY → CSV
// =====================================================

export function exportHistoryToCSV(items: AccountPrompt[], accountName: string): void {
  const escCSV = (val: string) => `"${val.replace(/"/g, '""')}"`

  const headers = ['#', 'Date', 'Account', 'Asked By', 'Question', 'Response', 'Model', 'Tokens', 'HubSpot Logged']
  const rows = items.map((item, idx) => [
    (idx + 1).toString(),
    new Date(item.created_at).toISOString(),
    accountName,
    item.user_name || item.user_email,
    item.prompt,
    item.response,
    item.model || '',
    item.tokens_used?.toString() || '',
    item.hubspot_note_id ? 'Yes' : 'No',
  ])

  const csv = headers.join(',') + '\n' + rows.map(row => row.map(escCSV).join(',')).join('\n')
  downloadCSV(csv, `DealHub_AI_History_${accountName.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`)
}

// =====================================================
// HELPERS
// =====================================================

function downloadCSV(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  window.URL.revokeObjectURL(url)
}

// =====================================================
// EXPORT FORMAT DETECTION
// =====================================================

export type ExportFormat = 'pdf' | 'csv'

const CSV_KEYWORDS = [
  'csv', 'spreadsheet', 'give me a sheet', 'excel',
  'data export', 'as a sheet', 'in a sheet', 'table of',
  'list of all', 'export the data', 'export as',
]

const PDF_KEYWORDS = [
  'report', 'pdf', 'document', 'research report',
  'generate a report', 'summary report', 'analysis report',
  'briefing', 'brief', 'one-pager', 'download as',
  'as a document', 'write up',
]

export function detectExportIntent(prompt: string): ExportFormat | null {
  const lower = prompt.toLowerCase()

  // CSV keywords checked first — more specific intent
  for (const keyword of CSV_KEYWORDS) {
    if (lower.includes(keyword)) return 'csv'
  }

  for (const keyword of PDF_KEYWORDS) {
    if (lower.includes(keyword)) return 'pdf'
  }

  return null
}

// =====================================================
// TARGET ACCOUNTS CSV EXPORT
// =====================================================

function getOwnerNameForExport(ownerId: string | null, owners: Owner[]): string {
  if (!ownerId) return ''
  const owner = owners.find((o) => o.id === ownerId)
  if (owner) {
    return owner.first_name && owner.last_name
      ? `${owner.first_name} ${owner.last_name}`
      : owner.email?.split('@')[0] || 'Unknown'
  }
  return ''
}

export function exportAccountsToCSV(accounts: TargetAccount[], owners: Owner[]): void {
  const escCSV = (val: string) => `"${val.replace(/"/g, '""')}"`

  const headers = [
    'Name',
    'Domain',
    'Sector',
    'Country',
    'City',
    'Owner',
    'Industry',
    'Employees',
    'Revenue',
    'ICP Tier',
    'Overall ICP Score',
    'Volume Score',
    'International Score',
    'Multi-Product Score',
    'Conversion Score',
    'Status',
    'Lifecycle Stage',
    'Contacts',
    'Deals',
    'Last Contacted',
  ]

  const rows = accounts.map((account) => [
    account.name || '',
    account.domain || '',
    account.sector || '',
    account.country || '',
    account.city || '',
    getOwnerNameForExport(account.hubspot_owner_id, owners),
    account.industry || '',
    account.employees?.toString() || '',
    account.revenue?.toString() || '',
    account.icp_tier || '',
    account.overall_icp_score || '',
    account.volume_score || '',
    account.international_score || '',
    account.multiproduct_score || '',
    account.icp_fit_score || '',
    account.account_status || 'active',
    account.lifecyclestage || '',
    account.num_associated_contacts?.toString() || '',
    account.num_associated_deals?.toString() || '',
    account.notes_last_contacted || '',
  ])

  const csv = headers.join(',') + '\n' + rows.map((row) => row.map(escCSV).join(',')).join('\n')
  const dateFile = new Date().toISOString().split('T')[0]
  downloadCSV(csv, `DealHub_Target_Accounts_${dateFile}.csv`)
}

// =====================================================
// HELPERS
// =====================================================

function addFooter(doc: jsPDF): void {
  const totalPages = doc.getNumberOfPages()
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i)
    const pageHeight = doc.internal.pageSize.getHeight()
    const pageWidth = doc.internal.pageSize.getWidth()
    doc.setFontSize(7)
    doc.setTextColor(160, 160, 160)
    doc.text(
      `Generated by DealHub AI Assistant  |  Page ${i} of ${totalPages}`,
      pageWidth / 2,
      pageHeight - 8,
      { align: 'center' }
    )
  }
}
